/**
 * Available meta tags for customisation
 */
export enum MetaTag {
    OG_URL = 'og:url',
    OG_SITE_NAME = 'og:site_name',
    OG_IMAGE = 'og:image',
    OG_IMAGE_ALT = 'og:image:alt',
    OG_TITLE = 'og:title',
    OG_TYPE = 'og:type',
    OG_DESCRIPTION = 'og:description',
    FB_APP_ID = 'fb:app_id',
    FB_PAGE_ID = 'fb:page_id',
    DESCRIPTION = 'description',
    KEYWORDS = 'keywords',
    ROBOTS = 'robots',
    PRERENDER_STATUS_CODE = 'prerender-status-code',
}

/**
 * Describes a meta tag element
 */
export interface IMetaTag {
    name: MetaTag | string;
    content: string;
}

/**
 * The meta service assists in setting different
 * types of page meta tags.
 */
export class MetaService {
    private metas: Map<string | MetaTag, string> = new Map();

    /**
     * Sets default meta information to fall back to
     * @param meta
     */
    setDefaultTagInformation(meta: IMetaTag) {
        this.metas.set(meta.name, meta.content);
        this.setMetaTag(meta);
    }

    /**
     * Sets a meta tag with the values provided
     * @param meta
     */
    setMetaTag(meta: IMetaTag) {
        const content = meta.content || this.metas.get(meta.name);

        if (!content) {
            this.removeTag(meta.name);
            return;
        }

        const tag = this.findOrCreateTag(meta.name);
        tag.setAttribute('content', content);
    }

    /**
     * Sets the page title
     * @param title
     */
    setTitle(title: string) {
        if (!title) {
            return;
        }

        const titleTag = document.querySelector('title')!;
        titleTag.innerText = title;
    }

    /**
     * Gets the current meta tag or creates one
     * @param name
     */
    private findOrCreateTag(name: string) {
        const tag = this.getTag(name);

        if (tag) {
            return tag;
        }

        // Create new tag as none exists
        const createdTag = document.createElement('meta');

        if (this.isProperty(name)) {
            createdTag.setAttribute('property', name);
        } else {
            createdTag.setAttribute('name', name);
        }

        document.head.appendChild(createdTag);

        return createdTag;
    }

    /**
     * Removes an existing tag from the document.
     * @param name
     */
    private removeTag(name: string) {
        const tag = this.getTag(name);

        if (!tag) {
            return;
        }

        tag.parentElement!.removeChild(tag);
    }

    /**
     * Gets a meta tag from the document
     * @param name
     */
    private getTag(name: string) {
        if (this.isProperty(name)) {
            return document.querySelector(`meta[property='${name}']`);
        }

        return document.querySelector(`meta[name='${name}']`);
    }

    /**
     * Checks if meta tag name should use property attribute
     * or not.
     * @param name
     */
    private isProperty(name: string) {
        return name.startsWith('og:') || name.startsWith('fb:');
    }
}

export const meta = new MetaService();
